import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Users, Snowflake, ArrowUpRight } from "lucide-react";
import InquiryDialog from "@/components/InquiryDialog";
import { Button } from "@/components/ui/button";

const CATEGORIES = [
  { key: "all", label: "All", match: () => true },
  { key: "sedan", label: "Sedan", match: (v, catName) => /sedan|dzire|swift|amaze/i.test(catName + " " + v.name) },
  { key: "suv", label: "SUV", match: (v, catName) => /suv|fortuner|scorpio|ertiga|tavera/i.test(catName + " " + v.name) },
  { key: "innova", label: "Innova", match: (v) => /innova/i.test(v.name) },
  { key: "traveller", label: "Traveller", match: (v, catName) => /tempo|traveller/i.test(catName + " " + v.name) },
  { key: "urbania", label: "Urbania", match: (v) => /urbania/i.test(v.name) },
  { key: "bus", label: "Bus", match: (v, catName) => /bus|coach|volvo/i.test(catName + " " + v.name) },
];

export default function ChooseYourVehicle() {
  const [cat, setCat] = useState("all");
  const { data: vehicles = [] } = useQuery({
    queryKey: ["cyv-vehicles"],
    queryFn: () => api.get("/vehicles", { params: { is_active: true, limit: 100 } }).then((r) => r.data.items),
  });
  const { data: cats = [] } = useQuery({
    queryKey: ["cyv-cats"],
    queryFn: () => api.get("/vehicle-categories", { params: { is_active: true } }).then((r) => r.data.items),
  });
  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);

  const filtered = useMemo(() => {
    const rule = CATEGORIES.find((c) => c.key === cat) || CATEGORIES[0];
    return vehicles.filter((v) => rule.match(v, catById[v.category_id]?.name || ""));
  }, [vehicles, cat, catById]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24" data-testid="choose-vehicle-section">
      <div className="text-center mb-8">
        <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-3">Our Fleet</div>
        <h2 className="font-display text-4xl sm:text-5xl text-slate-950 leading-tight">Choose Your Vehicle</h2>
        <p className="mt-4 text-slate-600 max-w-2xl mx-auto">From comfortable sedans to spacious buses, choose the right vehicle for your journey.</p>
      </div>

      {/* Category tabs — horizontal scroll on mobile */}
      <div className="-mx-4 px-4 sm:mx-0 sm:px-0 mb-8">
        <div className="flex gap-2 overflow-x-auto no-scrollbar snap-x">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              data-testid={`cyv-filter-${c.key}`}
              className={`whitespace-nowrap snap-start px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                cat === c.key
                  ? "bg-brand-navy-900 text-white border-brand-navy-900"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="py-16 text-center text-slate-500">No vehicles in this category.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.slice(0, 8).map((v) => (
            <div
              key={v.id}
              data-testid={`cyv-card-${v.slug}`}
              className="rounded-2xl overflow-hidden bg-white border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={v.image_url} alt={v.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-600">{catById[v.category_id]?.name}</div>
                <div className="mt-1 font-display text-lg text-slate-950 leading-tight">{v.name}</div>
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {v.seats} Seats</span>
                  {v.ac && <span className="inline-flex items-center gap-1"><Snowflake className="w-3.5 h-3.5" /> AC</span>}
                </div>
                <div className="mt-3 text-sm">
                  {v.price_hint ? (
                    <div><span className="text-slate-500">Starting from </span><span className="text-brand-navy-900 font-semibold">{v.price_hint}</span></div>
                  ) : (
                    <div className="text-slate-600 font-medium">Get a Quote</div>
                  )}
                </div>
                <div className="mt-4 flex gap-2">
                  <Link to={`/transportation/${v.slug}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full rounded-full" data-testid={`cyv-view-${v.slug}`}>View Details</Button>
                  </Link>
                  <InquiryDialog defaultMessage={`Hello! I'd like to book a ${v.name}.`} sourceRef={v.slug} source="choose_vehicle" triggerTestId={`cyv-book-${v.slug}`}>
                    <Button size="sm" className="flex-1 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid={`cyv-book-${v.slug}`}>Book Now</Button>
                  </InquiryDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 text-center">
        <Link to="/transportation">
          <Button variant="outline" className="rounded-full" data-testid="cyv-view-all">
            View All Vehicles <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
    </section>
  );
}
